import { useEffect, useState, Fragment, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { executeQuery } from "../../../utils";
import LicenseQuoteCard from "./LicenseQuoteCard";
import { getFilePath } from "../../../utils";
import {
  IconBrandFacebookFilled,
  IconBrandTwitterFilled,
} from "@tabler/icons-react";
import UpgradeLicenseQuoteForm from "./LicenseQuoteForm";
import {
  IRenewalData,
  TLicenseOption,
  rangeToFullType,
} from "../../utils/quoteUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "../../../shared/useAnalytics";
import { DmLoadingSpinner } from "../../utils/functions";
import { IndividualQuoteCard } from "./IndividualQuoteCard";
import { useDocumentTitle } from "usehooks-ts";
import { ConfirmNumStudentsModal } from "./ConfirmNumStudentsModal";

const QUERY_KEY = "renewalData";

const UpgradeLicenseQuote = () => {
  const queryClient = useQueryClient();
  const moreOptionsSectionRef = useRef<HTMLAnchorElement>(null);
  const { pageView } = useAnalytics();
  const location = useLocation();
  const [showUpgradeForm, setShowUpgradeForm] = useState<boolean>(false);
  const [upgradeClicked, setUpgradeClicked] = useState<
    TLicenseOption | undefined
  >(undefined);
  const [currentLicense, setCurrentLicense] = useState<
    TLicenseOption | undefined
  >(undefined);
  const [recommendedUpgrade, setRecommendedUpgrade] = useState<
    TLicenseOption | undefined
  >(undefined);
  const [schoolToDistSameScope, setSchoolToDistSameScope] = useState<
    Array<TLicenseOption>
  >([]);
  const [entireDistrict, setEntireDistrict] = useState<Array<TLicenseOption>>(
    []
  );

  const [schoolOptionsForIndividual, setSchoolOptionsForIndividual] = useState<
    [TLicenseOption, TLicenseOption] | TLicenseOption
  >();

  const [districtOptionsForIndividual, setDistrictOptionsForIndividual] =
    useState<Array<[TLicenseOption, TLicenseOption] | TLicenseOption>>([]);

  const [quoteStatus, setQuoteStatus] = useState<
    "" | "loading" | "pdf" | "json" | "error" | "renewingFromTrial"
  >("");

  const [entity, setEntity] = useState<
    "school" | "district" | "individual" | ""
  >("");

  const { renewalId } = useParams();

  const {
    data: renewalData,
    isError,
    isSuccess: showCards,
    isLoading,
  } = useQuery<IRenewalData>({
    queryKey: [QUERY_KEY],
    queryFn: () =>
      executeQuery({
        path: `/public/renewal/data/${encodeURIComponent(renewalId || "")}`,
      }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnReconnect: false,
  });

  useDocumentTitle(
    renewalData?.licenseName
      ? `${renewalData.licenseName} | DeltaMath`
      : "DeltaMath"
  );

  const [showNumStudentsModal, setShowNumStudentsModal] = useState(false);

  const getPDF = async (requestBody: Record<string, unknown>) => {
    if (!requestBody.renewalToken) {
      requestBody.renewalToken = encodeURIComponent(renewalId || "");
    }
    if (!requestBody.currentLicenseType && renewalData?.type) {
      requestBody.currentLicenseType = rangeToFullType[renewalData.type];
    }
    if (typeof requestBody.currentHasIntegral === "undefined") {
      requestBody.currentHasIntegral = renewalData?.hasIntegral ? true : false;
    }
    const res = await executeQuery({
      path: "/quote_request",
      method: "post",
      requestBody,
      additionalHeaders: {
        Accept: "application/pdf",
      },
      responseType: "blob",
      returnHeaders: true,
    });

    const { responseData, responseHeaders } = res;

    if (responseHeaders["content-type"] === "application/pdf" && responseData) {
      return { responseData, responseHeaders };
    } else {
      const jsonResponse = await jsonifyBlob(responseData);
      return { responseData: jsonResponse, responseHeaders };
    }
  };

  const setDownload = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const jsonifyBlob = async (blob: Blob) => {
    try {
      return await new Response(blob).json();
    } catch (e) {
      return { success: false, message: `Error: ${e}` };
    }
  };

  const requestQuote = useMutation({
    mutationFn: getPDF,
    onSuccess: (response) => {
      const { responseData, responseHeaders } = response;

      if (
        responseHeaders["content-type"] === "application/pdf" &&
        responseData
      ) {
        setShowUpgradeForm(false);
        let fileName: any =
          responseHeaders["content-disposition"].match(/filename="([^"]+)"/);
        if (Array.isArray(fileName) && fileName.length > 1) {
          fileName = fileName[1];
        } else {
          fileName = `DeltaMath Quote ${new Date().toLocaleDateString()}.pdf`;
        }
        setQuoteStatus("");
        setDownload(responseData as Blob, fileName);
      } else if (responseData) {
        if ((responseData as any).success) {
          setQuoteStatus("json");
        } else {
          setQuoteStatus("error");
        }
      }
    },
    onError: (error) => {
      console.error("Error downloading PDF:", error);
      setQuoteStatus("error");
    },
  });

  const closeDialog = () => {
    setShowUpgradeForm(false);
    if (quoteStatus !== "loading") {
      setQuoteStatus("");
    }
  };

  useEffect(() => {
    pageView(
      {},
      {
        plugins: {
          google_tag_manager: true,
        },
      }
    );
  }, [location, pageView]);

  //[MB] The API has gone through a couple iterations, below I am going to format the data to more easily work with the component props as defined. This isn't entirely out of laziness - I think this will be a little more declatatiive and will give us one place to make changes if the API needs to change again (hopefully)
  useEffect(() => {
    if (renewalData?.type === "school_custom" || renewalData?.isIndividualSNN) {
      setShowNumStudentsModal(true);
    }
    if (renewalData && renewalData.licenseOptions.length > 0) {
      switch (renewalData.type) {
        case "school":
        case "school_custom":
          setEntity("school");
          break;
        case "individual":
          setEntity("individual");
          break;
        case "6-12":
        case "7-12":
        case "9-12":
        case "6-8":
        case "7-8":
          setEntity("district");
          break;
        default:
          setEntity("");
          break;
      }
      if (renewalData.type === "individual") {
        const licenses = [...renewalData.licenseOptions];
        if (renewalData.siteTypeForIndividual === "district") {
          setDistrictOptionsForIndividual(renewalData.licenseOptions);
        } else if (renewalData.siteTypeForIndividual === "school") {
          setSchoolOptionsForIndividual(licenses.shift());
          if (licenses.length > 0) {
            setDistrictOptionsForIndividual(licenses);
          }
        } else {
          setSchoolOptionsForIndividual(licenses[0]);
        }
      } else if (!Array.isArray(renewalData.licenseOptions[0])) {
        const licenseOptions = renewalData.licenseOptions as TLicenseOption[];
        setCurrentLicense(licenseOptions[0]);
        if (licenseOptions.length > 2) {
          setSchoolToDistSameScope([licenseOptions[1]]);
          setEntireDistrict([licenseOptions[2]]);
        } else if (licenseOptions.length > 1) {
          setEntireDistrict([licenseOptions[1]]);
        }
      } else {
        const licenseOptions = renewalData.licenseOptions as Array<
          [TLicenseOption, TLicenseOption]
        >;
        setCurrentLicense(licenseOptions[0][0]);
        setUpgradeClicked(licenseOptions[0][0]);
        setRecommendedUpgrade(licenseOptions[0][1]);
        if (licenseOptions.length > 2) {
          setSchoolToDistSameScope(licenseOptions[1]);
          setEntireDistrict(licenseOptions[2]);
        } else if (licenseOptions.length > 1) {
          setEntireDistrict(licenseOptions[1]);
        }
      }
    }
  }, [renewalData]);

  return (
    <Fragment>
      {showCards && (
        <UpgradeLicenseQuoteForm
          showUpgradeForm={showUpgradeForm}
          currentLicense={currentLicense}
          selectedUpgrade={upgradeClicked}
          currentIntegration={renewalData?.integrations}
          downloadPDF={requestQuote.mutate}
          quoteStatus={quoteStatus}
          setQuoteStatus={setQuoteStatus}
          closeDialog={closeDialog}
        />
      )}

      {renewalData && !renewalData.snnStudentCount && !!renewalData.nces && (
        <ConfirmNumStudentsModal
          previousNumStudents={renewalData?.numberOfStudents}
          grades={
            renewalData?.lowGrade && renewalData?.highGrade
              ? `${renewalData.lowGrade}-${renewalData.highGrade}`
              : undefined
          }
          school={renewalData?.licenseName}
          visible={showNumStudentsModal}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
            setShowNumStudentsModal(false);
          }}
        />
      )}

      <header className="w-full">
        <div className="m-4">
          <a href="https://www.deltamath.com/">
            <img
              className="block h-8 w-auto"
              src={getFilePath("/images/DeltaMath-Logo_Blue.png")}
              alt="DeltaMath"
            />
          </a>
        </div>
      </header>

      <div className="flex min-h-screen flex-col justify-between overflow-hidden bg-dm-background-blue-100 px-4">
        {isLoading && (
          <div className="flex h-screen w-full items-center justify-center">
            <span className="-translate-y-16">
              <DmLoadingSpinner message="" />
            </span>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center space-y-6 px-4 py-10">
            <h1 className="text-center font-serif text-2xl font-bold text-dm-gray-800">
              Please Reach Out!
            </h1>
            <p className="w-full max-w-xl text-center text-xl text-dm-gray-500">
              We are unable to automatically show you all of your renewal
              options here, possibly because your district has a custom license.
              Please feel free to reach out to DeltaMath support or to use one
              of the{" "}
              <a
                className="text-decoration-line text-dm-brand-blue-500"
                href="https://www.deltamath.com/teachers-schools"
              >
                the quote request forms from our homepage.
              </a>
            </p>
          </div>
        )}

        {showCards && (
          <div className="mt-10 flex flex-col items-center gap-10">
            {renewalData.licenseName && (
              <div>
                <h1 className="mb-1 text-center font-serif text-lg font-bold text-dm-gray-800 sm:text-2xl">
                  {renewalData?.licenseName}
                </h1>
                <p className="text-md text-center text-dm-gray-500 sm:text-lg">
                  {recommendedUpgrade ||
                  schoolToDistSameScope.length > 0 ||
                  entireDistrict.length > 0
                    ? "Request a quote to upgrade or renew your current license."
                    : "Request a quote to renew your current license."}
                </p>
              </div>
            )}

            <div className="flex w-full flex-col-reverse items-center justify-center gap-10 lg:flex-row lg:gap-6">
              {renewalData.type === "individual" && (
                <IndividualQuoteCard
                  individualPlus={renewalData.individualPlus}
                  individualIntegral={renewalData.individualIntegral}
                  pricePerPlus={renewalData.pricePerPlus ?? 0}
                  pricePerIntegral={renewalData.pricePerIntegral ?? 0}
                  schoolYear={renewalData.schoolYear}
                  nces={renewalData.nces}
                />
              )}

              {currentLicense && (
                <LicenseQuoteCard
                  license={currentLicense}
                  upgradeStatus="current"
                  setShowUpgradeForm={setShowUpgradeForm}
                  setSelectedUpgrade={setUpgradeClicked}
                  downloadPDF={requestQuote.mutate}
                  setQuoteStatus={setQuoteStatus}
                  quoteStatus={quoteStatus}
                  integralTrial={renewalData?.integral_trial || false}
                  includeSparkle={currentLicense.tier === "Integral"}
                  includeFeatures
                />
              )}

              {recommendedUpgrade && (
                <LicenseQuoteCard
                  license={recommendedUpgrade}
                  upgradeStatus="upgrade"
                  recommended={true}
                  setShowUpgradeForm={setShowUpgradeForm}
                  setSelectedUpgrade={setUpgradeClicked}
                  downloadPDF={requestQuote.mutate}
                  setQuoteStatus={setQuoteStatus}
                  quoteStatus={quoteStatus}
                  integralTrial={renewalData?.integral_trial || false}
                  includeFeatures
                  includeSparkle
                />
              )}
            </div>

            {renewalData?.siteTypeForIndividual === "school" &&
              schoolOptionsForIndividual && (
                <div className="flex w-full flex-col items-center gap-6">
                  <h2 className="max-w-xl text-center font-serif text-lg font-bold text-dm-gray-800 sm:text-2xl sm:leading-10">
                    {districtOptionsForIndividual.length > 0
                      ? `Explore More License Options for ${
                          renewalData.licenseName
                        } and ${renewalData.districtName || "Your District"}`
                      : `Explore More License Options for ${renewalData.licenseName}`}
                  </h2>
                  <div className="flex w-full flex-col items-center justify-center gap-6 lg:flex-row">
                    {Array.isArray(schoolOptionsForIndividual) ? (
                      schoolOptionsForIndividual.map(
                        (license: TLicenseOption, i: number) => {
                          return (
                            <LicenseQuoteCard
                              key={`ind-school-${i}`}
                              license={license}
                              upgradeStatus="upgrade"
                              recommended={false}
                              setShowUpgradeForm={setShowUpgradeForm}
                              setSelectedUpgrade={setUpgradeClicked}
                              downloadPDF={requestQuote.mutate}
                              setQuoteStatus={setQuoteStatus}
                              quoteStatus={quoteStatus}
                              integralTrial={
                                renewalData?.integral_trial || false
                              }
                              includeFeatures={true}
                            />
                          );
                        }
                      )
                    ) : (
                      <LicenseQuoteCard
                        key={`ind-school`}
                        license={schoolOptionsForIndividual}
                        upgradeStatus="upgrade"
                        recommended={false}
                        setShowUpgradeForm={setShowUpgradeForm}
                        setSelectedUpgrade={setUpgradeClicked}
                        downloadPDF={requestQuote.mutate}
                        setQuoteStatus={setQuoteStatus}
                        quoteStatus={quoteStatus}
                        integralTrial={renewalData?.integral_trial || false}
                        includeFeatures={true}
                      />
                    )}
                  </div>
                </div>
              )}

            {renewalData?.type === "individual" &&
              districtOptionsForIndividual.length > 0 && (
                <div className="flex w-full flex-col items-center gap-6">
                  {renewalData.siteTypeForIndividual === "district" && (
                    <h2 className="max-w-xl text-center font-serif text-lg font-bold text-dm-gray-800 sm:text-2xl sm:leading-10">
                      {`Explore More License Options for ${renewalData?.licenseName}`}
                    </h2>
                  )}
                  {districtOptionsForIndividual.map(
                    (
                      licenseOption:
                        | TLicenseOption
                        | [TLicenseOption, TLicenseOption],
                      i: number
                    ) => {
                      if (Array.isArray(licenseOption)) {
                        return (
                          <div
                            className="flex w-full flex-col items-center justify-center gap-6 lg:flex-row"
                            key={`ind-dist-${i}`}
                          >
                            {licenseOption.map(
                              (license: TLicenseOption, j: number) => {
                                return (
                                  <LicenseQuoteCard
                                    key={`ind-dist-${i}-${j}`}
                                    license={license}
                                    upgradeStatus="upgrade"
                                    recommended={false}
                                    setShowUpgradeForm={setShowUpgradeForm}
                                    setSelectedUpgrade={setUpgradeClicked}
                                    downloadPDF={requestQuote.mutate}
                                    setQuoteStatus={setQuoteStatus}
                                    quoteStatus={quoteStatus}
                                    integralTrial={
                                      renewalData?.integral_trial || false
                                    }
                                    includeFeatures={
                                      renewalData.siteTypeForIndividual ===
                                        "district" && i === 0
                                    }
                                  />
                                );
                              }
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <LicenseQuoteCard
                            key={`ind-dist-${licenseOption.tier}`}
                            license={licenseOption}
                            upgradeStatus="upgrade"
                            recommended={false}
                            setShowUpgradeForm={setShowUpgradeForm}
                            setSelectedUpgrade={setUpgradeClicked}
                            downloadPDF={requestQuote.mutate}
                            setQuoteStatus={setQuoteStatus}
                            quoteStatus={quoteStatus}
                            integralTrial={renewalData?.integral_trial || false}
                            includeFeatures={
                              renewalData.siteTypeForIndividual ===
                                "district" && i === 0
                            }
                          />
                        );
                      }
                    }
                  )}
                </div>
              )}

            {renewalData?.type === "individual" &&
              renewalData.licenseOptions.length === 0 && (
                <div className="flex w-full flex-col items-center gap-6">
                  <p className="text-md text-center text-dm-gray-500 sm:text-lg">
                    Interested in a license for your school?<br></br> Please
                    reach out to us at{" "}
                    <a
                      href="mailto:orders@deltamath.com"
                      className="text-dm-brand-blue-500 underline"
                    >
                      orders@deltamath.com
                    </a>{" "}
                    for more information.
                  </p>
                </div>
              )}

            {(schoolToDistSameScope.length > 0 ||
              entireDistrict.length > 0) && (
              <div className="flex w-full flex-col items-center gap-6">
                <span
                  ref={moreOptionsSectionRef}
                  className="block -translate-y-6"
                />
                <h2 className="max-w-xl text-center font-serif text-lg font-bold text-dm-gray-800 sm:text-2xl sm:leading-10">
                  {entity === "school"
                    ? renewalData.districtName
                      ? `Explore More License Options for ${renewalData.districtName}`
                      : "Explore More License Options for Your Entire District"
                    : `Explore More License Options for ${renewalData?.licenseName}`}
                </h2>

                {renewalData.type !== "individual" && (
                  <button
                    className="fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-dm-brand-blue-500 text-white transition-colors duration-150 hover:bg-dm-brand-blue-600"
                    onClick={() => {
                      moreOptionsSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                  >
                    <i className="far fa-chevron-down" />
                  </button>
                )}

                {schoolToDistSameScope.length > 0 && (
                  <div className="flex w-full flex-col items-center justify-center gap-6 lg:flex-row">
                    {schoolToDistSameScope.map(
                      (license: TLicenseOption, i: number) => {
                        return (
                          <LicenseQuoteCard
                            key={`Same-level-${i}`}
                            license={license}
                            upgradeStatus="upgrade"
                            recommended={false}
                            setShowUpgradeForm={setShowUpgradeForm}
                            setSelectedUpgrade={setUpgradeClicked}
                            downloadPDF={requestQuote.mutate}
                            setQuoteStatus={setQuoteStatus}
                            quoteStatus={quoteStatus}
                            integralTrial={renewalData?.integral_trial || false}
                            includeFeatures={false}
                          />
                        );
                      }
                    )}
                  </div>
                )}

                {entireDistrict.length > 0 && (
                  <div className="flex w-full flex-col items-center justify-center gap-6 lg:flex-row">
                    {entireDistrict.map(
                      (license: TLicenseOption, i: number) => {
                        return (
                          <LicenseQuoteCard
                            key={`entire-dist-${i}`}
                            license={license}
                            upgradeStatus="upgrade"
                            recommended={false}
                            setShowUpgradeForm={setShowUpgradeForm}
                            setSelectedUpgrade={setUpgradeClicked}
                            downloadPDF={requestQuote.mutate}
                            setQuoteStatus={setQuoteStatus}
                            quoteStatus={quoteStatus}
                            integralTrial={renewalData?.integral_trial || false}
                            includeFeatures={false}
                          />
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="footer__container flex-center-space-evenly-center-column !mt-32">
          <div className="footer__logo flex-center-center-center-row">
            <a href="https://www.deltamath.com/">
              <img
                className="footer__logo-img"
                src={getFilePath("/images/DeltaMath-Logo_Blue.png")}
                alt="DeltaMath Footer logo"
              />
            </a>
          </div>
          <div className="footer__navbar flex-center-center-center-row">
            <ul className="footer__nav-links flex-center-space-evenly-center-row">
              <li className="footer__nav-link" id="teachersFLink">
                <a href="https://www.deltamath.com/teachers-schools">
                  Teachers&#x200a;/&#x200a;Schools
                </a>
              </li>
              <li className="footer__nav-link" id="studentsFLink">
                <a href="https://www.deltamath.com/students">
                  Parents/Students
                </a>
              </li>
              <li className="footer__nav-link" id="aboutFLink">
                <a href="https://www.deltamath.com/about">About</a>
              </li>
              <li className="footer__nav-link" id="faqsFLink">
                <a href="https://www.deltamath.com/faqs">FAQs/Videos</a>
              </li>
              <li className="footer__nav-link" id="contactFLink">
                <a href="https://www.deltamath.com/contact">Contact</a>
              </li>
            </ul>
          </div>
          <div className="footer__bottom flex-center-space-between-center-row">
            <div className="footer__bottom-copyright md:text-md p-4 text-xs">
              <span>
                &#169; {new Date().getFullYear()} DeltaMath Solutions Inc.
              </span>
              <a
                href="https://www.deltamath.com/terms-policies"
                target="_blank"
                rel="noreferrer"
              >
                Terms & Policies
              </a>
              <a href="https://status.deltamath.com">Site Status</a>
            </div>
            <div className="footer__bottom-social flex-center-space-between-center-row">
              <a
                href="https://www.facebook.com/DeltaMathWebsite/"
                id="facebook-social"
              >
                <IconBrandFacebookFilled aria-label="Facebook" />
              </a>
              <a href="https://twitter.com/MrDeltaMath" id="twitter-social">
                <IconBrandTwitterFilled aria-label="Twitter" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default UpgradeLicenseQuote;
